export type CloudinaryAssetType = 'image' | 'video';

export type CloudinaryQuality =
  | 'auto'
  | 'auto:best'
  | 'auto:good'
  | 'auto:eco'
  | 'auto:low'
  | 'auto:low:sensitive'
  | number;

export interface CloudinaryAssetOptions {
  /** The asset path/key including version and folder structure (e.g., "v1720342452/ttp/bg-login_yacvm1.png") */
  key: string;
  /** Asset type determines the upload path */
  type: CloudinaryAssetType;
  /** Quality transformation - defaults to 'auto' for images, undefined for videos */
  quality?: CloudinaryQuality;
  /** Additional transformations like 'fl_preserve_transparency' */
  transformations?: string[];
}

/**
 * Cloudinary Asset Service
 * Generates standardized Cloudinary URLs for images and videos
 */
export class CloudinaryAssetService {
  private static readonly BASE_URL = 'https://res.cloudinary.com';

  /**
   * Get the cloudinary folder ID from environment
   * Automatically detects environment based on available globals
   */
  private static getCloudinaryFolder(): string {
    // Check if we're in a Vite environment (hub/admin apps)
    if (
      typeof import.meta !== 'undefined' &&
      // avoid TypeScript error in Next.js
      'env' in import.meta &&
      (import.meta as any).env?.['VITE_CLOUDINARY_FOLDER']
    ) {
      return (import.meta as any).env['VITE_CLOUDINARY_FOLDER'];
    } else if (
      typeof process !== 'undefined' &&
      process.env?.['NEXT_PUBLIC_CLOUDINARY_FOLDER']
    ) {
      return process.env['NEXT_PUBLIC_CLOUDINARY_FOLDER'];
    }
    // Fallback for apps that don't set environment variables properly
    // This matches the hardcoded value found in the codebase
    return 'dvt6xbblx';
  }

  /**
   * Generate a Cloudinary asset URL
   */
  static generateUrl({
    key,
    type,
    quality,
    transformations = [],
  }: CloudinaryAssetOptions): string {
    const cloudinaryFolder = this.getCloudinaryFolder();
    const uploadPath = `/${type}/upload`;

    // Build transformation string
    const transformationParts: string[] = [];

    // Add quality for images (auto by default), skip for videos unless explicitly set
    if (type === 'image' && quality !== undefined) {
      transformationParts.push(`q_${quality}`);
    } else if (type === 'video' && quality !== undefined) {
      transformationParts.push(`q_${quality}`);
    } else if (type === 'image' && quality === undefined) {
      // Default to auto quality for images
      transformationParts.push('q_auto:good');
    }

    // Add format auto for images by default
    if (type === 'image') {
      transformationParts.push('f_auto');
    }

    // Add any additional transformations
    transformationParts.push(...transformations);

    // Build the final URL
    const transformationString =
      transformationParts.length > 0 ? `/${transformationParts.join(',')}` : '';

    return `${this.BASE_URL}/${cloudinaryFolder}${uploadPath}${transformationString}/${key}`;
  }

  /**
   * Generate an image URL with default optimizations
   */
  static image(
    key: string,
    options?: Omit<CloudinaryAssetOptions, 'key' | 'type'>,
  ): string {
    return this.generateUrl({
      key,
      type: 'image',
      ...options,
    });
  }

  /**
   * Generate a video URL
   */
  static video(
    key: string,
    options?: Omit<CloudinaryAssetOptions, 'key' | 'type'>,
  ): string {
    return this.generateUrl({
      key,
      type: 'video',
      ...options,
    });
  }
}

// Export convenience functions
export const cloudinaryImage = (
  key: string,
  options?: { quality?: CloudinaryQuality; transformations?: string[] },
) => CloudinaryAssetService.image(key, options);

export const cloudinaryVideo = (
  key: string,
  options?: { quality?: CloudinaryQuality; transformations?: string[] },
) => CloudinaryAssetService.video(key, options);
